const hasOwn = (object, key) =>
  object != null && Object.prototype.hasOwnProperty.call(object, key);

const matchingKey = (object, key, view) => {
  if (!object) return null;
  if (hasOwn(object, key)) return key;
  if (view !== "country" || typeof key !== "string") return null;
  const lower = key.toLowerCase();
  return (
    Object.keys(object).find(
      (candidate) => candidate.toLowerCase() === lower,
    ) ?? null
  );
};

const hasCellStates = (row) =>
  row?.cellStates !== null &&
  typeof row?.cellStates === "object" &&
  !Array.isArray(row.cellStates);

// Country dim keys arrive as "country:<key>"; response cells are keyed by
// the bare Country.key. Shared with DataTableGrid so tests can exercise the
// real strip-plus-casing combination end to end.
export const stripDimKey = (k, view) =>
  view === "country" && typeof k === "string" && k.startsWith("country:")
    ? k.slice("country:".length)
    : k;

export const resolveCellPresentation = ({ row, key, view }) => {
  const valueKey = matchingKey(row?.cells, key, view);
  const value = valueKey === null ? undefined : row.cells[valueKey];

  // A finite scalar always renders, exactly as before this feature; the
  // qualitative fallback only decides what the non-numeric glyph means.
  if (Number.isFinite(value)) return { kind: "value", text: null, value };
  if (row?.qualitative) return { kind: "qualitative", text: "n.a", value };

  if (!hasCellStates(row)) {
    return { kind: "legacy_na", text: "n.a", value };
  }

  const stateKey = matchingKey(row.cellStates, key, view);
  const state = stateKey === null ? undefined : row.cellStates[stateKey];
  if (state === "na_reported") {
    return { kind: "na_reported", text: "n.a", value };
  }
  if (state === "not_submitted") {
    return { kind: "not_submitted", text: "-", value };
  }
  if (state === "value_unavailable") {
    return { kind: "value_unavailable", text: "n.a", value };
  }
  if (valueKey === null && stateKey === null) {
    return { kind: "not_in_scope", text: "-", value };
  }
  // Contract violation ("value" without a finite scalar) or a state this
  // build does not know: render the safe glyph and explain nothing rather
  // than assert a confident, possibly false cause.
  return { kind: "unknown_state", text: "n.a", value };
};

export const getCellStateNote = ({ presentation, t }) => {
  switch (presentation?.kind) {
    case "qualitative":
      return t("Qualitative metric. It has no numeric value.");
    case "legacy_na":
      return t("No numeric value is available for this data column.");
    case "na_reported":
      return t(
        "Reported without a numeric value, including answers marked n.a.",
      );
    case "value_unavailable":
      return t("Submitted values could not be consolidated.");
    case "not_submitted":
      return t("Nothing was submitted for this data column.");
    case "not_in_scope":
      return t("This metric is not assigned in the current scope.");
    default:
      return null;
  }
};

export const getRowDataNote = ({ row, dimKeys, view, t }) => {
  if (row.qualitative) {
    return t("Qualitative metric. It has no numeric value.");
  }

  const kinds = new Set(
    dimKeys.map(
      (key) =>
        resolveCellPresentation({
          row,
          key: stripDimKey(key, view),
          view,
        }).kind,
    ),
  );
  if (kinds.has("legacy_na")) {
    return t("n.a means no numeric value is available for that column.");
  }

  const notes = [];
  if (kinds.has("na_reported")) {
    notes.push(
      t(
        "In the data columns, n.a means the metric was reported without a numeric value, including answers marked n.a.",
      ),
    );
  }
  if (kinds.has("value_unavailable")) {
    // Self-contained when it is the only n.a sentence; "also" is only
    // grammatical after the reported-n.a sentence above.
    notes.push(
      kinds.has("na_reported")
        ? t("n.a can also mean submitted values could not be consolidated.")
        : t(
            "In the data columns, n.a means submitted values could not be consolidated.",
          ),
    );
  }
  if (kinds.has("not_submitted") || kinds.has("not_in_scope")) {
    notes.push(
      t(
        "A dash means nothing was submitted, or the metric is not assigned in this scope.",
      ),
    );
  }
  return notes.length > 0 ? notes.join(" ") : null;
};

export const formatMetricPageLabel = ({ total, page, totalPages, t }) =>
  total === 1
    ? t("1 metric · Page {{page}} of {{totalPages}}", { page, totalPages })
    : t("{{total}} metrics · Page {{page}} of {{totalPages}}", {
        total,
        page,
        totalPages,
      });
